import useAuth from '../../modules/auth/hooks/useAuth'
import { useEffect, useState } from 'react'
import { Meeting, MeetingBotStatus } from '../../modules/meeting/model/meetingInterfaces'
import useFetchMeeting from '../../modules/meeting/hooks/useFetchMeeting'
import useStartRecording from '../../modules/meeting/hooks/useStartRecording'
import useStopRecording from '../../modules/meeting/hooks/useStopRecording'
import zoomSdk from '@zoom/appssdk'
import useRegenerateSummary from '../../modules/meeting/hooks/useRegenerateSummary'
import useMeetingAssist from '../../modules/meeting/hooks/useMeetingAssist'
import useAssistFeedback from '../../modules/meeting/hooks/useAssistFeedback'
import useAssistNow from '../../modules/meeting/hooks/useAssistNow'
import MyMeetingsPage from '../my-meetings/MyMeetingsPage'
import InMeetingPage from '../in-meeting/InMeetingPage'

const enum ContentPage {
  MainClient = 'inMainClient',
  Meeting = 'inMeeting',
  NoMeeting = 'noMeeting',
  Loading = 'loading',
}

const HomePage: React.FC = () => {
  const { context } = useAuth()
  const [contentPage, setContentPage] = useState<ContentPage>(ContentPage.Loading)
  const [hasInterval, setHasInterval] = useState(false)
  const { isFetching, fetchMeeting } = useFetchMeeting()
  const { isLoading: isRegenerateLoading, regenerateSummary } = useRegenerateSummary()
  const { isStarting, startRecording } = useStartRecording()
  const { isStopping, stopRecording } = useStopRecording()

  const [activeMeeting, setActiveMeeting] = useState<Meeting | null>(null)
  const assistCues = useMeetingAssist(
    activeMeeting?.id ?? null,
    activeMeeting?.botStatus === MeetingBotStatus.Recording,
  )
  const { feedbackById, submitFeedback } = useAssistFeedback(activeMeeting?.id ?? null)
  const { isRequesting: isAssistNowLoading, requestAssistNow } = useAssistNow()

  const toggleRecording = async () => {
    if (!activeMeeting) {
      console.error('No active meeting found')
      return
    }
    if (activeMeeting.botStatus === MeetingBotStatus.Recording) {
      await stopRecording(activeMeeting.id)
    } else {
      await startRecording(activeMeeting.id)
    }
    await refreshState()
  }

  const toggleRegenerateSummary = async () => {
    if (!activeMeeting || isRegenerateLoading) {
      return
    }
    await regenerateSummary(activeMeeting.id)
    refreshState()
  }

  const refreshState = async () => {
    if (isFetching || isStarting || isStopping) {
      // do not refresh state while bot is joining or leaving or while fetching
      return
    }

    if (context?.runningContext && context?.runningContext === ContentPage.MainClient) {
      setContentPage(ContentPage.MainClient)
      return
    }

    let meetingContext, meetingUrl

    try {
      meetingContext = await zoomSdk.getMeetingContext()
      meetingUrl = await zoomSdk.getMeetingJoinUrl()
    } catch (e) {
      console.warn('Error getting sdk response', e)
    }

    if (!meetingContext || !meetingUrl) {
      console.warn('Failed to get meeting context or join url')
      setContentPage(ContentPage.NoMeeting)
      return
    }

    const meeting = await fetchMeeting({
      zoomId: meetingContext.meetingID,
      url: meetingUrl.joinUrl,
      metadata: {
        title: meetingContext?.meetingTopic,
      },
    })

    setContentPage(ContentPage.Meeting)
    setActiveMeeting(meeting)
  }

  useEffect(() => {
    refreshState()
  }, [])

  useEffect(() => {
    if (contentPage === ContentPage.Meeting && !hasInterval) {
      setHasInterval(true)
      const interval = setInterval(refreshState, 10000)

      return () => {
        clearInterval(interval)
        setHasInterval(false)
      }
    }
  }, [contentPage])

  if (contentPage === ContentPage.Loading) {
    return <div>Loading...</div>
  }

  if (contentPage === ContentPage.NoMeeting) {
    return (
      <div className="p-4">
        <h1 className="text-2xl font-bold">You are not in a Zoom meeting 🧐 </h1>
        <p>Start a meeting and open this app to generate your meeting smart transcript 🚀</p>
      </div>
    )
  }

  if (contentPage === ContentPage.MainClient) {
    return <MyMeetingsPage />
  }

  return (
    <InMeetingPage
      activeMeeting={activeMeeting}
      assistCues={assistCues}
      feedbackById={feedbackById}
      submitFeedback={submitFeedback}
      requestAssistNow={requestAssistNow}
      isAssistNowLoading={isAssistNowLoading}
      toggleRecording={toggleRecording}
      toggleRegenerateSummary={toggleRegenerateSummary}
      isLoading={isFetching}
      isRegenerateLoading={isRegenerateLoading}
      onMeetingUpdated={(meeting) => setActiveMeeting(meeting)}
    />
  )
}

export default HomePage
